/**
 * Persist storage adapter that maps each zustand state field to its own
 * localStorage key, preserving the exact keys and JSON format the legacy
 * store.js package used. Existing sessions hydrate without migration, and a
 * rollback to a store.js build reads the same data.
 */
export function createLegacyStorage(keys) {
  return {
    getItem: () => {
      const state = {};
      for (const key of keys) {
        const raw = localStorage.getItem(key);
        let value;
        if (raw !== null) {
          try {
            value = JSON.parse(raw);
          } catch {
            // Malformed JSON reads as undefined, matching store.js behavior.
          }
        }
        // Absent keys are set to undefined explicitly so rehydrating merges
        // them over (and clears) stale in-memory values.
        state[key] = value;
      }
      return { state, version: 0 };
    },
    setItem: (_name, { state }) => {
      for (const key of keys) {
        if (state[key] === undefined) {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, JSON.stringify(state[key]));
        }
      }
    },
    removeItem: () => keys.forEach(key => localStorage.removeItem(key)),
  };
}

/**
 * Rehydrate a persisted store when another tab/window writes one of its keys.
 * store.js read localStorage fresh on every get, so cross-window consumers
 * (dialer/monitor windows, the axios layer in other tabs) rely on this.
 * storage events only fire in other tabs, so this cannot echo-loop.
 */
export function syncAcrossTabs(store, keys) {
  window.addEventListener("storage", e => {
    if (e.key === null || keys.includes(e.key)) {
      store.persist.rehydrate();
    }
  });
}
